import RainfallLineChart from "../Components/HistoricalData/RainfallLineChart";
import TemperatureBarChart from "../Components/HistoricalData/TemperatureBarChart";
import HumidityChart from "../Components/HistoricalData/HumidityChart";
import LocationSelector from "../Components/Common/LocationSelector.jsx";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { formatDate } from "../AllFunctions/formattedDateAndTime";
import { setHistoricWeatherData, setPlaceName } from "../ReduxStore/App/DashBoard/dashboardSlice";
import getPlaceName from "../api/PlaceName";
import HistoricData from "../api/HistoricData.js";
import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";

export default function HistoricalWeatherData() {
  const { historicWeatherData, longitude, latitude } =
    useSelector((store) => store.dashboard);
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);

  const addError = (errorMessage) => {
    setErrors((prev) => [...prev, errorMessage])
  }

  const { placeName } = useSelector((store) => store.dashboard);

  async function getHistoricWeatherData(lat, lng) {
    let errorMessage = null;
    try {
      let res = await HistoricData(lat, lng);
      const historicData = res['historical_data']
      if (historicData) {
        dispatch(setHistoricWeatherData(historicData));
      } else {
        errorMessage = 'Invalid response format from HistoricData API'
        console.error(errorMessage);
        dispatch(setHistoricWeatherData([]));
        throw new Error(errorMessage)
      }
    } catch (error) {
      console.log("error:", error);
      dispatch(setHistoricWeatherData([]));
      throw new Error(errorMessage ? errorMessage : "Could not fetch Formatted Weather Data")
    }
  }
  async function getPlaceWithLatLng(lat, lng) {
    let errorMessage = null;
    try {
      const res = await getPlaceName(lat, lng);
      if (res && res.location) {
        dispatch(setPlaceName(res.location.village, res.location.sub_dist));
      } else {
        errorMessage = 'Invalid response format from Place name lookup'
        throw new Error("")
      }
    } catch (error) {
      console.log("error:", error);
      throw new Error(errorMessage ? errorMessage : "Place name lookup failed");
    }
  }

  async function loadData() {
    // Only fetch data if we don't have formattedWeather data or if coordinates changed
    if (latitude && longitude) {
      setLoading(true);
      setErrors([]);
      const results = await Promise.allSettled([
        getHistoricWeatherData(latitude, longitude),
        getPlaceWithLatLng(latitude, longitude)
      ]);

      results.forEach((result) => {
        if (result.status === "rejected") {
          addError(result.reason.message || "Unknown error occurred");
        }
      });
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData()
  }, [latitude, longitude]);

  if (loading) {
    return (
      <div className="h-full w-full bg-primaryBgColor dark:bg-darkModePrimaryBgColor rounded-md overflow-hidden relative ">
        <Skeleton
          height="100%"
          baseColor="transparent"
          highlightColor="rgba(255, 255, 255, 0.2)"
        />
      </div>
    );
  }

  if (errors.length > 0) {
    return (
      <div className="flex items-center justify-center h-screen bg-primaryBgColor dark:bg-darkModePrimaryBgColor">
        <div className="bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200 px-6 py-5 rounded-md shadow-md max-w-md">
          <h2 className="text-lg font-semibold mb-3"> Failed to load data</h2>
          <ul className="list-disc list-inside text-sm mb-4 space-y-1">
            {errors.map((err, idx) => (<li key={idx}>{err}</li>))}
          </ul>
          <button onClick={loadData} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition" > Try Again </button>
        </div>
      </div>
    );
  }
  return (
    <div className="h-full rounded-lg bg-primaryBgColor dark:bg-darkModePrimaryBgColor p-4 custom-scrollbar overflow-y-auto">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row justify-between items-center mb-6 bg-cardBgColor dark:bg-darkModeSecondaryBgColor rounded-lg border border-gray-200 dark:border-white/10 shadow-sm p-4 gap-4">
        <div className="text-center sm:text-left">
          <h1 className="text-xl font-extrabold text-blackPrimaryText dark:text-darkModeBlackPrimaryText tracking-tight uppercase">
            Historical Insights
          </h1>
          <p className="text-xs font-bold text-blackPrimaryText dark:text-darkModeBlackPrimaryText opacity-60 mt-1">
            Fortnight: {historicWeatherData && historicWeatherData.length > 0 ? `${formatDate(historicWeatherData[0]?.Date_time)} to ${formatDate(historicWeatherData[historicWeatherData.length - 1]?.Date_time)}` : "No data available"}
          </p>
        </div>
        <LocationSelector />
      </div>

      <div className="space-y-6">
        {/* Charts Section 1 */}
        <div className="bg-cardBgColor dark:bg-darkModeCardBgColor border border-gray-200 dark:border-white/10 rounded-lg p-6 shadow-sm hover:shadow-md transition-all duration-300">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="h-[40vh] w-full">
              <RainfallLineChart historicData={historicWeatherData} />
            </div>
            <div className="h-[40vh] w-full">
              <TemperatureBarChart historicData={historicWeatherData} />
            </div>
          </div>
        </div>

        {/* Charts Section 2 */}
        <div className="bg-cardBgColor dark:bg-darkModeCardBgColor border border-gray-200 dark:border-white/10 rounded-lg p-6 shadow-sm hover:shadow-md transition-all duration-300">
          <div className="h-[40vh] w-full">
            <HumidityChart historicData={historicWeatherData} />
          </div>
        </div>
      </div>
    </div>
  );
}
